<template>
	<div class="px-5">
		<app-table
			compact
			class="action-list-table is-not-selectable mt-5"
			name="action-list"
			:columns="columns"
			:column-widths="columnWidths"
			:current-page="currentPage"
			:rows="rows"
			:last-page="lastPage"
			@on-search-event="onSearch"
			@on-page="changePage"
		>
			<template slot="rows" slot-scope="props">
				<td>
					<div
						class="action-icon-tile"
						:class="{ 'action-icon-tile-filled': props.row.pathIcon }"
						:title="props.row.pathIcon || 'Sin ícono'"
					>
						<img
							v-if="props.row.pathIcon"
							:src="props.row.pathIcon"
							:alt="`Ícono de ${props.row.name}`"
							@error="hideBrokenIcon"
						>
						<span class="action-icon-initials">{{ iconInitials(props.row) }}</span>
					</div>
				</td>
				<td class="action-list-text-cell" :title="actionTooltip(props.row)">
					<div class="action-name-cell">
						<strong>{{ props.row.name }}</strong>
						<small v-if="secondaryName(props.row)">{{ secondaryName(props.row) }}</small>
					</div>
				</td>
				<td>
					<span class="action-code-chip" :title="props.row.code">{{ props.row.code }}</span>
				</td>
				<td class="action-list-text-cell" :title="props.row.codeWeb">
					<code class="action-uri">{{ props.row.codeWeb }}</code>
				</td>
				<td>
					<div class="action-languages-cell">
						<span
							v-for="language in languageEntries(props.row)"
							:key="`${props.row.id}-${language.key}`"
							class="action-language-chip"
							:class="{ 'action-language-chip-filled': language.name }"
							:title="languageTooltip(language)"
						>
							<v-icon>{{ language.name ? 'check_circle' : 'error_outline' }}</v-icon>
							{{ language.code }}
						</span>
					</div>
				</td>
				<td>
					<div class="action-details-cell">
						<span
							v-if="isNewAction(props.row)"
							class="action-badge action-badge-new"
							:title="newTitleText(props.row) || 'Marcada como nueva'"
						>
							<v-icon>fiber_manual_record</v-icon>
							{{ newTitleText(props.row) || 'Nuevo' }}
						</span>
						<a
							v-for="(manual, indexManual) in manuals(props.row)"
							:key="`${props.row.id}-manual-${indexManual}`"
							class="action-badge action-badge-manual"
							:href="manual.file"
							target="_blank"
							rel="noopener noreferrer"
							:title="`Manual ${manual.code}`"
						>
							<v-icon>picture_as_pdf</v-icon>
							{{ manual.code }}
						</a>
						<span
							v-if="hotKeys(props.row).length"
							class="action-badge action-badge-keys"
							:title="`Teclas rápidas: ${hotKeys(props.row).join(', ')}`"
						>
							<v-icon>keyboard</v-icon>
							{{ hotKeys(props.row).join(' + ') }}
						</span>
						<span
							v-if="links(props.row).length"
							class="action-badge action-badge-link"
							:title="`Enlace: ${links(props.row).join(' · ')}`"
						>
							<v-icon>link</v-icon>
							{{ links(props.row).length }}
						</span>
						<span v-if="!hasDetails(props.row)" class="action-details-empty">—</span>
					</div>
				</td>
				<td>{{ props.row.numFragment }}</td>
				<td>{{ props.row.position }}</td>
				<td>
					<app-tooltip-button
						color="green"
						icon="edit"
						text="Editar"
						@click-icon="openEdit(props.row)"
					/>
					<app-tooltip-button
						color="red"
						icon="delete"
						text="Eliminar"
						@click-icon="openModalConfirmation(props.row)"
					/>
				</td>
			</template>
		</app-table>
		<app-confirmation-modal
			:loading="loading"
			:show-modal="modalConfirmation.value"
			@cancel="cancelModalConfirmation"
			@confirm="confirm()"
		/>
	</div>
</template>

<script>
import appConfirmationModal from '@/components/shared/app-confirmation-modal';
import appTooltipButton from '@/components/shared/app-tooltip-button';

const LANGUAGE_ORDER = ['es', 'en'];

function changePage(page) {
	this.$emit('change-page', page);
}

function confirm() {
	const headers = {
		Accept: process.env.ACCEPT_SERVICE_V2,
	};
	this.loading = true;
	this.$http
		.delete(`action-bar/${this.modalConfirmation.selectedRow.id}`, { headers })
		.then(() => {
			this.showNotification('Registro eliminado correctamente');
		})
		.catch((err) => {
			if (err.status === 405) {
				if (err.data.message === 'RESOURCE_HAS_ASSOCIATED_DATA') {
					this.showError('La acción no pudo ser eliminada por tener data asociada.');
				}
			}
		})
		.finally(() => {
			this.loading = false;
			this.modalConfirmation.value = false;
			this.$emit('on-delete');
		});
}

function languageEntries(row) {
	const languages = (row && row.languages) || {};
	const keys = Object.keys(languages);
	const ordered = LANGUAGE_ORDER.filter(key => keys.indexOf(key) > -1)
		.concat(keys.filter(key => LANGUAGE_ORDER.indexOf(key) === -1));
	return ordered.map((key) => {
		const language = languages[key] || {};
		return {
			key,
			code: String(language.id || key).toUpperCase(),
			name: String(language.name || '').trim(),
			description: String(language.description || '').trim(),
			link: String(language.link || '').trim(),
			manuals: (language.manuals || []).filter(manual => manual && manual.file),
		};
	});
}

function languageTooltip(language) {
	const parts = [`${language.code}: ${language.name || 'Sin nombre'}`];
	if (language.description && language.description !== language.name) {
		parts.push(language.description);
	}
	if (language.manuals.length) {
		parts.push(`${language.manuals.length} manual(es)`);
	}
	return parts.join(' · ');
}

function secondaryName(row) {
	const entries = this.languageEntries(row);
	const other = entries.find(language => language.name && language.name !== row.name);
	return other ? `${other.code}: ${other.name}` : '';
}

function actionTooltip(row) {
	return this.languageEntries(row).map(this.languageTooltip).join('\n');
}

function manuals(row) {
	return this.languageEntries(row).reduce((list, language) => list.concat(
		language.manuals.map(manual => ({ code: language.code, file: manual.file })),
	), []);
}

function links(row) {
	return this.languageEntries(row)
		.filter(language => language.link && language.link !== '.')
		.map(language => `${language.code}: ${language.link}`);
}

function hotKeys(row) {
	return ((row && row.hotKeys) || [])
		.map(key => String(typeof key === 'object' && key ? key.key || key.name || '' : key).trim())
		.filter(key => key);
}

function isNewAction(row) {
	return Boolean(row && Number(row.flagNew));
}

function newTitleText(row) {
	const newTitle = (row && row.newTitle) || {};
	const titles = Object.keys(newTitle)
		.map(key => String((newTitle[key] || {}).title || '').trim())
		.filter(title => title);
	return titles.length ? titles[0] : '';
}

function hasDetails(row) {
	return this.isNewAction(row) ||
		Boolean(this.manuals(row).length) ||
		Boolean(this.hotKeys(row).length) ||
		Boolean(this.links(row).length);
}

function iconInitials(row) {
	return String((row && row.code) || '-').substring(0, 2).toUpperCase();
}

function hideBrokenIcon({ target }) {
	target.classList.add('is-hidden');
	if (target.parentNode) {
		target.parentNode.classList.remove('action-icon-tile-filled');
	}
}

function onSearch(value) {
	this.$emit('on-search', value);
}

function openEdit(row) {
	this.$emit('open-edit', row);
}

function data() {
	return {
		columnWidths: {
			8: '62px',
			1: '19%',
			2: '9%',
			3: '16%',
			6: '11%',
			9: '15%',
			4: '8%',
			5: '7%',
			7: '11%',
		},
		loading: false,
	};
}

export default {
	name: 'action-list',
	components: {
		appConfirmationModal,
		appTooltipButton,
	},
	data,
	props: {
		columns: {
			type: Array,
			required: true,
		},
		currentPage: {
			type: Number,
			default: 0,
		},
		lastPage: {
			type: Number,
			default: 0,
		},
		rows: {
			type: Array,
			required: true,
		},
	},
	methods: {
		actionTooltip,
		changePage,
		confirm,
		hasDetails,
		hideBrokenIcon,
		hotKeys,
		iconInitials,
		isNewAction,
		languageEntries,
		languageTooltip,
		links,
		manuals,
		newTitleText,
		onSearch,
		openEdit,
		secondaryName,
	},
};
</script>

<style lang="scss">
.action-list-table .table-list {
	table-layout: fixed;

	> tbody > tr,
	> tbody > tr > td {
		height: 56px !important;
		min-height: 56px !important;
	}

	.action-list-text-cell {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.action-icon-tile {
	align-items: center;
	background: linear-gradient(135deg, #2b435b 0%, #3f6187 100%);
	border-radius: 9px;
	box-shadow: 0 3px 8px rgba(43, 67, 91, .22);
	display: inline-flex;
	height: 38px;
	justify-content: center;
	overflow: hidden;
	position: relative;
	width: 38px;

	img {
		height: 24px;
		object-fit: contain;
		position: relative;
		width: 24px;
		z-index: 1;

		&.is-hidden { display: none; }
	}
}

.action-icon-tile-filled .action-icon-initials {
	display: none;
}

.action-icon-initials {
	color: rgba(255, 255, 255, .85);
	font-size: 12px;
	font-weight: 700;
	left: 0;
	letter-spacing: .5px;
	position: absolute;
	text-align: center;
	top: 50%;
	transform: translateY(-50%);
	width: 100%;
}

.action-name-cell {
	display: flex;
	flex-direction: column;
	line-height: 1.2;
	overflow: hidden;
	text-align: left;

	strong {
		color: #2b435b;
		font-size: 13px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	small {
		color: #8495a5;
		font-size: 10px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.action-code-chip {
	background: #eef3f8;
	border: 1px solid #dbe4ed;
	border-radius: 6px;
	color: #45617e;
	display: inline-block;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: .4px;
	max-width: 100%;
	overflow: hidden;
	padding: 3px 8px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.action-uri {
	background: transparent;
	box-shadow: none;
	color: #6b7c8d;
	font-size: 11px;
	font-weight: 500;

	&::before, &::after { content: none; }
}

.action-languages-cell {
	align-items: center;
	display: flex;
	gap: 5px;
	justify-content: center;
}

.action-language-chip {
	align-items: center;
	background: #f0f2f5;
	border: 1px solid #dde3ea;
	border-radius: 11px;
	color: #8495a5;
	display: inline-flex;
	font-size: 10px;
	font-weight: 700;
	gap: 3px;
	line-height: 1;
	padding: 3px 7px 3px 5px;

	.icon { color: #b4bfca; font-size: 13px; }
}

.action-language-chip-filled {
	background: #dff3e6;
	border-color: #bde3cb;
	color: #23834a;

	.icon { color: #2eaa64; }
}

.action-details-cell {
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	justify-content: center;
}

.action-badge {
	align-items: center;
	background: #eef3f8;
	border: 1px solid #dbe4ed;
	border-radius: 10px;
	color: #53697d;
	display: inline-flex;
	font-size: 10px;
	font-weight: 700;
	gap: 3px;
	line-height: 1;
	padding: 3px 7px 3px 5px;
	text-decoration: none;

	.icon { font-size: 13px; }
}

.action-badge-new {
	background: #fdf1dc;
	border-color: #f3ddb4;
	color: #a6711a;

	.icon { color: #e19a18; }
}

.action-badge-manual {
	background: #fdecec;
	border-color: #f5cfcf;
	color: #b44141;

	.icon { color: #d35252; }
	&:hover { background: #fadada; }
}

.action-badge-keys {
	background: #eef0fc;
	border-color: #d5daf5;
	color: #4a54a8;

	.icon { color: #6b76d1; }
}

.action-badge-link .icon { color: #176fc1; }

.action-details-empty {
	color: #b4bfca;
	font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/actions/action-list.vue